<template>
  <div class="overflow-hidden card h-100">
    <div class="pt-3 pb-0 pe-0 card-body">
      <div class="row">
        <div class="col-sm-6">
          <div class="d-flex">
            <div class="ms-3">
              <div
                class="mb-0 text-sm text-uppercase text-primary font-weight-bold"
              >
                {{ dateRange }}
              </div>
              <h5 class="mb-0 font-weight-bolder mt-1">Recovery {{this.lastSynInfo}}</h5>
            </div>
          </div>
        </div>
        <div class="col-sm-6 d-flex justify-content-end mt-3">
          <div class="btn-group me-4" role="group" data-toggle="buttons">
            <label
              v-for="(option, index) in dayOptions"
              :id="index"
              :key="option.name"
              class="btn btn-outline-white"
              :class="{ active: activeIndex === index }"
            >
              <input
                type="radio"
                name="options"
                class="btn-check"
                autocomplete="off"
                :checked="activeIndex === index"
                @click="initChart(index)"
              />
              <span class="d-none d-sm-block p-1">{{ option.name }}</span>
            </label>
          </div>
        </div>

        <div class="col-12">
          <user-recovery-chart
            v-if="chart"
            id="user-recovery-chart"
            title=""
            :chart="chart"
            height="400"
          />
        </div>

        <hr class="hr horizontal dark my-3" />
        <div class="col-12">
          <div class="d-flex">
            <div class="ms-3">
              <div
                class="mb-0 text-sm text-uppercase text-primary font-weight-bold"
              >
                {{ dateRange }}
              </div>
              <h5 class="mb-0 font-weight-bolder mt-1">
                Resting Heart Rate (RHR)
              </h5>
            </div>
          </div>
        </div>
        <div class="col-12">
          <user-rhr-chart
            v-if="chart"
            id="user-rhr-chart"
            title=""
            :chart="chartRhr"
            height="400"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
const dayjs = require("dayjs");
import Constants from "@/helpers/constants.js";
import UserRecoveryChart from "../components/UserRecoveryChart.vue";
import UserRhrChart from "../components/UserRhrChart.vue";

export default {
  name: "UserRecovery",
  components: {
    UserRecoveryChart,
    UserRhrChart,
  },
  props: {
    title: {
      type: String,
      default: "Recovery",
    },
    udid: {
      type: String,
      required: true,
    },
    selectedDate: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      activeIndex: 1,
      options: [30, 14, 7],
      chart: null,
      userRecoveryData: [],
      workoutTypes: Constants.WORKOUT_TYPES,
      chartRhr: null,
      rhrData: [],
      lastSyncDate: '',
      lastSynInfo: '',
    };
  },

  computed: {
    dayOptions() {
      let values = [];
      this.options.forEach((opt) => {
        values.push({ name: `${opt} days` });
      });
      return values;
    },
    dateRange() {
      let maxDate = dayjs(this.selectedDate);
      let daysCount = this.options[this.activeIndex];

      return `Last ${daysCount} days : ${maxDate
        .subtract(daysCount - 1, "days")
        .format(Constants.CHART_DATE_FORMAT)} to ${maxDate.format(
        Constants.CHART_DATE_FORMAT
      )}`;
    },
  },
  watch: {
    udid() {
      this.initChart(this.activeIndex);
    },
    selectedDate() {
      this.getRecoveryData();
    },
  },
  mounted() {
    if (this.udid) this.initChart(this.activeIndex);
  },
  methods: {
    initChart(index) {
      this.activeIndex = index;
      this.getRecoveryData();
      this.getRhrData();
    },
    getRecoveryData() {
      let daysCount = this.options[this.activeIndex];

      this.$store
        .dispatch("data/userRecoveryData", {
          udid: this.udid,
          dt_from: dayjs(this.selectedDate)
            .startOf("day")
            .subtract(daysCount - 1, "day")
            .format(Constants.BUCKET_FORMAT),
          dt_to: dayjs(this.selectedDate)
            .startOf("day")
            .format(Constants.BUCKET_FORMAT),
        })
        .then((data) => {
          this.userRecoveryData = data.data;
          this.lastSyncDate = dayjs(data.lastSyncDate).format(Constants.TABLE_DOB_FORMAT);
          if(dayjs(this.selectedDate).isAfter(dayjs(this.lastSyncDate))) {
            this.lastSynInfo = '- (Last Sync Date: '+this.lastSyncDate+')';
          } else {
            this.lastSynInfo = '';
          }

          let chartDays = [];
          let recoveryValues = [];
          let hrvValues = [];
          let rhrValues = [];

          this.userRecoveryData.map((record) => {
            recoveryValues.push(record.recovery_score ? (record.recovery_score >= 0 ? record.recovery_score : 0 ) : 0);
            hrvValues.push(record.hrv ? record.hrv : 0);
            rhrValues.push(record.rhr ? record.rhr : 0);
            chartDays.push([
              dayjs(record.bucket).format(Constants.CHART_DATE_FORMAT),
              dayjs(record.bucket).format(
                Constants.CHART_DATE_DAY_OF_WEEK_FORMAT
              ),
            ]);
          });
          // Recovery chart
          this.chart = {
            labels: chartDays,
            datasets: [
              {
                label: "Recovery Score (%)",
                data: recoveryValues,
              },
              {
                label: "HRV",
                data: hrvValues,
              },
            ],
          };

          // RHR chart
          this.chartRhr = {
            labels: chartDays,
            datasets: [
              {
                label: "RHR (BPM)",
                data: rhrValues,
              },
            ],
          };
        });
    },
    getRhrData() {
      this.chartDays = [];
      this.rhrData = [];

      let days = this.options[this.activeIndex];
      let today = dayjs();
      // let from = today.subtract(days - 1, "days");

      // TODO: generate random data. Replace with api data in future.
      let dt = "";
      for (let day = days - 1; day >= 0; day--) {
        this.rhrData.push(Math.floor(Math.random() * 40) + 40);
        dt = today.subtract(day, "days");
        this.chartDays.push([
          dt.format(Constants.CHART_DATE_FORMAT),
          dt.format(Constants.CHART_DATE_DAY_OF_WEEK_FORMAT),
        ]);
      }

      this.chartRhr = {
        labels: this.chartDays,
        datasets: [
          {
            label: "RHR (BPM)",
            data: this.rhrData,
          },
        ],
      };
    },
  },
};
</script>
<style lang="scss" scoped>
.btn-outline-white {
  background: none;
}
label {
  &.active {
    color: $g-gray !important;
  }
}
.card .form-select {
  background-color: $color-background-info-pane;
}
div .bg-dark h5 {
  color: $lighter !important;
}
</style>
